import { HelpCircle, MessageSquare, Users, X } from 'lucide-react';
import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import { ChatPanel } from './ChatPanel';
import type { ChatPanelProps } from './ChatPanel';
import { QAPanel } from './QAPanel';
import type { QAPanelProps } from './QAPanel';
import { ViewersPanel } from './ViewersPanel';
import type { ViewersPanelProps } from './ViewersPanel';

/** The three webinar right-panel tabs. */
export type WebinarPanelTab = 'chat' | 'qa' | 'viewers';

export interface WebinarSidePanelProps {
  readonly isMobile: boolean;
  /** Controlled open state for the mobile overlay (desktop keeps the column always on). */
  readonly open: boolean;
  readonly onClose: () => void;
  /** Own identity: chat authorship and the «вы» markers. */
  readonly ownIdentity: string | null;
  readonly chat: Omit<ChatPanelProps, 'ownIdentity'>;
  readonly qa: QAPanelProps;
  readonly viewers: ViewersPanelProps;
}

/**
 * The webinar right panel (the mockup's aside): «Чат» / «Вопросы · N» / «Зрители» tabs over
 * the #101012 column. The chat tab reuses the meeting ChatPanel, Q&A and viewers are webinar
 * surfaces. On desktop it is a persistent 330px column; on mobile it becomes a full-screen
 * overlay sheet toggled from the webinar bottom bar.
 */
export function WebinarSidePanel(props: WebinarSidePanelProps): JSX.Element | null {
  const { t } = useTranslation();
  const [tab, setTab] = useState<WebinarPanelTab>('chat');
  if (props.isMobile && !props.open) {
    return null;
  }
  return (
    <aside
      data-testid="webinar-side-panel"
      className={cn(
        'flex flex-col border-l border-white/[0.06]',
        props.isMobile
          ? 'fixed inset-y-0 right-0 z-40 w-full animate-in slide-in-from-right-10 bg-zinc-950/95 backdrop-blur duration-200'
          : // min-w-0: BR-DM-2 - the desktop column must shrink, never force the row wide.
            'w-[330px] min-w-0 shrink-0',
      )}
      style={{ background: props.isMobile ? undefined : '#101012' }}
    >
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setTab(value as WebinarPanelTab);
        }}
        className="flex h-full min-h-0 flex-col gap-0"
      >
        {/* Same compression contract as SidePanel (BR-DM-1): the tab row scrolls horizontally
            so the close button never leaves the panel at phone widths. */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-white/[0.06] px-3 pt-2.5">
          <div className="scroll-thin-dark min-w-0 flex-1 overflow-x-auto">
            <TabsList className="h-8 w-fit min-w-full justify-start bg-white/[0.05] p-0.5">
              <TabsTrigger
                value="chat"
                data-testid="webinar-tab-chat"
                className="h-7 gap-1.5 rounded-md px-3 text-[12.5px] text-zinc-400 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white"
              >
                <MessageSquare className="size-3.5" />
                {t('webinar.tab_chat')}
              </TabsTrigger>
              <TabsTrigger
                value="qa"
                data-testid="webinar-tab-qa"
                className="h-7 gap-1.5 rounded-md px-3 text-[12.5px] text-zinc-400 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white"
              >
                <HelpCircle className="size-3.5" />
                {t('webinar.tab_qa')} · {props.qa.questions.length}
              </TabsTrigger>
              <TabsTrigger
                value="viewers"
                data-testid="webinar-tab-viewers"
                className="h-7 gap-1.5 rounded-md px-3 text-[12.5px] text-zinc-400 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white"
              >
                <Users className="size-3.5" />
                {t('webinar.tab_viewers')}
              </TabsTrigger>
            </TabsList>
          </div>
          {props.isMobile ? (
            <button
              type="button"
              data-testid="close-webinar-panel"
              aria-label={t('participants.close_aria')}
              onClick={props.onClose}
              className="grid size-11 shrink-0 place-items-center rounded-md text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
        <TabsContent value="chat" className="min-h-0 flex-1">
          <ChatPanel {...props.chat} ownIdentity={props.ownIdentity} />
        </TabsContent>
        <TabsContent value="qa" className="flex min-h-0 flex-1 flex-col">
          <QAPanel {...props.qa} />
        </TabsContent>
        <TabsContent value="viewers" className="flex min-h-0 flex-1 flex-col">
          <ViewersPanel {...props.viewers} />
        </TabsContent>
      </Tabs>
    </aside>
  );
}
